import { computed } from 'vue';
import { useUrlSourcesStore } from '@/store/urlSourcesStore';
import { useUrlsStore } from '@/store/urlsStore';
import { useCategoriesStore } from '@/store/categoriesStore';
import { useProductsStore } from '@/store/productsStore';
import { usePagesStore } from '@/store/pagesStore';

/**
 * Determines whether categories, products, or pages
 * are "complete" by comparing related store values
 * (plus a "text" version for your chips).
 */
export function useCompleteness() {
    const urlSourcesStore = useUrlSourcesStore();
    const urlsStore = useUrlsStore();

    const categoriesStore = useCategoriesStore();
    const productsStore = useProductsStore();
    const pagesStore = usePagesStore();

    // ------------------ Category ------------------ //
    const isCategoryComplete = computed(() => {
        return (
            urlsStore.getNumberOfCategoryUrls === urlSourcesStore.getNumberOfCategoryUrls &&
            urlsStore.getNumberOfCategoryUrls === categoriesStore.getNumberOfCategories
        );
    });

    const categoryText = computed(() => {
        // If complete, show short text; else partial
        if (isCategoryComplete.value) {
            return `Categories: ${categoriesStore.getNumberOfCategories}`;
        } else {
            return `Categories: ${urlsStore.getNumberOfCategoryUrls} / ${urlSourcesStore.getNumberOfCategoryUrls} - ${categoriesStore.getNumberOfCategories}`;
        }
    });

    // ------------------ Product ------------------ //
    const isProductComplete = computed(() => {
        return (
            urlsStore.getNumberOfProductUrls === productsStore.getNumberOfProducts
        );
    });

    const productText = computed(() => {
        if (isProductComplete.value) {
            // If "complete" is always true, that’s a sign that these store values match
            return `Products: ${productsStore.getNumberOfProducts}`;
        } else {
            return `Products: ${urlsStore.getNumberOfProductUrls} / ${urlSourcesStore.getNumberOfProductUrls} - ${productsStore.getNumberOfProducts}`;
        }
    });

    // ------------------ Page ------------------ //
    const isPageComplete = computed(() => {
        return (
            urlsStore.getNumberOfPageUrls === urlSourcesStore.getNumberOfPageUrls &&
            urlsStore.getNumberOfPageUrls === pagesStore.getNumberOfPages
        );
    });

    const pageText = computed(() => {
        if (isPageComplete.value) {
            return `Pages: ${pagesStore.getNumberOfPages}`;
        } else {
            return `Pages: ${urlsStore.getNumberOfPageUrls} / ${urlSourcesStore.getNumberOfPageUrls} - ${pagesStore.getNumberOfPages}`;
        }
    });

    return {
        // Booleans
        isCategoryComplete,
        isProductComplete,
        isPageComplete,

        // Text strings
        categoryText,
        productText,
        pageText
    };
}
